// @ts-strict-ignore
import React from 'react'
import { useAtomValue } from 'jotai'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { PAYMENT_METHODS_LABELS } from 'pages/Contacts/constants'
import { getAccountNumberAndBankCodeLabelKeysForBankAccount } from 'pages/Contacts/utils/fiatInstruments'
import { PaymentMethod } from 'types/beneficiaries'
import { ContactInstrument } from 'types/contacts'
import { sentenceCase } from 'utils/utils'
import {
  hasIntermediaryBankAddress as checkIntermediaryBankAddress,
  isIntermediaryBankDataPresent as checkIntermediaryBankData,
  formatAccountHolderAddress,
  formatBankAddress,
  formatIntermediaryBankAddress,
  getAccountHolderFullName,
  getTransferDestination,
  shouldShowIdentificationCode,
  shouldShowSwiftCode,
} from './bankAccountUtils'

interface BankAccountSidePanelBodyProps {
  contactInstrument: ContactInstrument
}

export const BankAccountSidePanelBody = ({ contactInstrument }: BankAccountSidePanelBodyProps) => {
  const { t } = useTranslation()
  const { data: countries, isPending: isLoadingCountries } = useCountriesQuery(true)
  const contact = useAtomValue(contactAtom)

  const {
    accountNumber,
    accountType,
    currency,
    bank,
    paymentMethods,
    intermediaryBanks,
    accountHolderAddress,
    accountHolderName,
    defaultRemittanceInformation,
  } = contactInstrument || {}

  // Extract bank details
  const { name: bankName, identificationCode, swiftCode, address: bankAddress } = bank || {}

  // Calculate derived values using helper functions
  const transferDestination = getTransferDestination(paymentMethods as PaymentMethod[])
  const isInternational = transferDestination === 'INTERNATIONAL'

  const accountNumberAndBankCodeLabelKeysForBankAccount =
    getAccountNumberAndBankCodeLabelKeysForBankAccount(
      accountNumber,
      currency,
      paymentMethods as PaymentMethod[]
    )

  const { formatted: bankAddressFormatted } = formatBankAddress(bankAddress, countries)
  const { formatted: accountHolderAddressFormatted } = formatAccountHolderAddress(
    accountHolderAddress ?? contact?.entity?.address,
    countries
  )
  const contactEntity = contact?.entity
  const accountHolderFullName = getAccountHolderFullName(accountHolderName, contactEntity)

  // Check display conditions
  const isAccountHolderDataPresent = !!accountHolderFullName || !!accountHolderAddressFormatted

  const intermediaryBank = intermediaryBanks?.[0]
  const hasIntermediaryAddress = checkIntermediaryBankAddress(intermediaryBank?.address)
  const isIntermediaryDataPresent = checkIntermediaryBankData(intermediaryBank)
  const showSwiftCode = shouldShowSwiftCode(currency, swiftCode, isInternational)
  const showIdentificationCode = shouldShowIdentificationCode(
    currency,
    identificationCode,
    isInternational
  )

  return isLoadingCountries ? (
    <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  ) : (
    <Box>
      {isAccountHolderDataPresent && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">
            {t('contacts.accountHolderDetails')}
          </h4>
          <Box paddingT={8} paddingB={12}>
            {accountHolderFullName && (
              <DataListItem
                id="account-holder-name"
                testId="account-holder-name"
                title={t('accountHolder')}
                body={accountHolderFullName}
              />
            )}
            {defaultRemittanceInformation && (
              <DataListItem
                id="payment-reference"
                testId="payment-reference"
                title={t('beneficiaryPaymentReference')}
                body={defaultRemittanceInformation}
                copyText={defaultRemittanceInformation}
              />
            )}
            {!!accountHolderAddressFormatted && (
              <DataListItem
                id="account-holder-address"
                testId="account-holder-address"
                title={t('manageBeneficiaries.beneficiaryAddress')}
                body={accountHolderAddressFormatted}
              />
            )}
          </Box>
        </Box>
      )}

      <h4 className="text-foreground-prominent text-h4">{t('bankDetails')}</h4>
      <Box paddingT={8} paddingB={12}>
        {accountNumber && (
          <DataListItem
            id="account-number"
            testId="account-number"
            title={t(
              accountNumberAndBankCodeLabelKeysForBankAccount?.accountNumberLabelKey ||
                'accountNumber'
            )}
            body={ellipseMiddleText(accountNumber, 8, 8)}
            copyText={accountNumber}
          />
        )}

        {showSwiftCode && (
          <DataListItem
            id="bank-code"
            testId="bank-code"
            title={t(
              accountNumberAndBankCodeLabelKeysForBankAccount?.swiftCodeLabelKey || 'bicSwiftCode'
            )}
            body={swiftCode}
            copyText={swiftCode}
          />
        )}

        {showIdentificationCode && (
          <DataListItem
            id="identification-code"
            testId="identification-code"
            title={t(
              accountNumberAndBankCodeLabelKeysForBankAccount?.bankCodeLabelKey || 'bicSwiftCode'
            )}
            body={identificationCode}
            copyText={identificationCode}
          />
        )}

        {accountType && (
          <DataListItem
            id="account-type"
            testId="account-type"
            title={t('accountType')}
            body={sentenceCase(accountType?.toLowerCase())}
          />
        )}
        {bankName && (
          <DataListItem
            id="bank-name"
            testId="bank-name"
            title={t('bankName')}
            body={startCase(bankName?.toLowerCase())}
          />
        )}
        {!!bankAddressFormatted && (
          <DataListItem
            id="bank-address"
            testId="bank-address"
            title={t('bankAddress')}
            body={bankAddressFormatted}
          />
        )}
        {paymentMethods && !!paymentMethods.length && (
          <DataListItem
            id="payment-methods"
            testId="payment-methods"
            title={t(paymentMethods.length > 1 ? 'paymentMethods' : 'paymentMethod')}
            body={paymentMethods
              ?.map(paymentMethod => t(PAYMENT_METHODS_LABELS[paymentMethod]))
              .join(', ')}
          />
        )}
      </Box>

      {isIntermediaryDataPresent && isInternational && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h4>
          <Box paddingT={8} paddingB={12}>
            {intermediaryBank?.swiftCode && (
              <DataListItem
                id="intermediary-bank-code"
                testId="intermediary-bank-code"
                title={t('bicSwiftCode')}
                body={intermediaryBank.swiftCode}
                copyText={intermediaryBank.swiftCode}
              />
            )}
            {intermediaryBank?.identificationCode && (
              <DataListItem
                id="intermediary-routing-number"
                testId="intermediary-routing-number"
                title={t('abaRoutingNumber')}
                body={intermediaryBank.identificationCode}
                copyText={intermediaryBank.identificationCode}
              />
            )}
            {intermediaryBank?.name && (
              <DataListItem
                id="intermediary-bank-name"
                testId="intermediary-bank-name"
                title={t('bankName')}
                body={intermediaryBank.name}
              />
            )}
            {hasIntermediaryAddress && (
              <DataListItem
                id="intermediary-bank-address"
                testId="intermediary-bank-address"
                title={t('bankAddress')}
                body={formatIntermediaryBankAddress(intermediaryBank?.address, countries)}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
